import { Box, Typography, useTheme } from "@mui/joy";
import { ReactNode } from "react";
import { palette } from "../../../theme";
import { CircularProgress } from "@mui/material";

interface StatCardProps {
  icon: ReactNode;
  title: string;
  value?: number;
  isTimeValue?: boolean;
  isLoading?: boolean;
}

const formatTimeDisplay = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

const StatCard = ({ icon, title, value, isTimeValue, isLoading }: StatCardProps) => {
  const theme = useTheme();
  
  const displayValue = isLoading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
      <CircularProgress size={24} />
    </Box>
  ) : isTimeValue ? (
    <Typography 
      level="h1" 
      sx={{ 
        my: 1,
        fontSize: { xs: '2rem', md: '2.5rem' },
        fontWeight: 800,
        color: palette.purple,
        lineHeight: 1,
        textShadow: `0 2px 10px ${palette.purple}30`,
      }}
    >
      {formatTimeDisplay(value || 0)}
    </Typography>
  ) : (
    <Typography 
      level="h1" 
      sx={{ 
        my: 1,
        fontSize: { xs: '2rem', md: '2.5rem' },
        fontWeight: 800,
        color: palette.purple,
        lineHeight: 1,
        textShadow: `0 2px 10px ${palette.purple}30`,
      }}
    >
      {value || 0}
    </Typography>
  );

  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: 4,
        p: { xs: 2.5, md: 3 },
        display: "flex",
        alignItems: "flex-start",
        gap: { xs: 2, md: 3 },
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
        },
        direction: "rtl",
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: `linear-gradient(90deg, ${palette.purple}, ${palette.purple}80)`,
          opacity: 0.8,
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: `${palette.purple}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
          "&:hover": {
            bgcolor: `${palette.purple}25`,
            transform: "scale(1.05)",
          },
          boxShadow: `0 4px 12px ${palette.purple}20`,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography 
          level="body-sm" 
          sx={{ 
            color: palette.gray,
            fontSize: '1rem',
            mb: 0.5,
            fontWeight: 600,
            letterSpacing: '0.01em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
        {displayValue}
      </Box>
    </Box>
  );
};

export default StatCard;
